import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  App,
  Button,
  Card,
  Descriptions,
  Popconfirm,
  Result,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { vendorApi, VendorNotFoundError, VendorUpdateError } from '@/utils/vendorApi';
import { VendorItem } from '@/types';
import { useAuthStore } from '@/store/authStore';
import { canArchiveVendor } from '@/store/vendorAccess';
import VendorEditForm from '@/components/vendor/VendorEditForm';
import ReinviteVendorButton from '@/components/vendor/ReinviteVendorButton';

const { Title } = Typography;

function formatTimestamp(value?: string): string {
  if (!value) {
    return '—';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY HH:mm') : '—';
}

function onboardingTag(status?: string) {
  const normalized = (status || '').toLowerCase();
  const color = normalized === 'completed' ? 'green' : normalized === 'pending' ? 'gold' : 'default';
  return <Tag color={color}>{status || '—'}</Tag>;
}

const OperatorVendorDetailPage: React.FC = () => {
  const { message } = App.useApp();
  const { authorization } = useAuthStore();
  const navigate = useNavigate();
  const { vendorId } = useParams<{ vendorId: string }>();
  const [searchParams] = useSearchParams();
  const hostname = searchParams.get('hostname') || 'default';
  const cancelledRef = useRef(false);

  const [vendor, setVendor] = useState<VendorItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const loadVendor = useCallback(async (showPageSpinner = true) => {
    const id = vendorId ? decodeURIComponent(vendorId) : '';

    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    if (showPageSpinner) {
      setLoading(true);
      setVendor(null);
    }
    setNotFound(false);
    setLoadError(false);
    try {
      const result = await vendorApi.getOperatorVendor(id, hostname);
      if (!cancelledRef.current) {
        setVendor(result);
      }
    } catch (error: unknown) {
      if (cancelledRef.current) {
        return;
      }
      if (error instanceof VendorNotFoundError) {
        setNotFound(true);
        message.error('Vendor not found');
      } else {
        setLoadError(true);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load vendor';
        message.error(errorMessage);
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  }, [vendorId, hostname, message]);

  useEffect(() => {
    cancelledRef.current = false;
    void loadVendor();
    return () => {
      cancelledRef.current = true;
    };
  }, [loadVendor]);

  const backButton = (
    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/my-vendors')}>
      Back to Vendors
    </Button>
  );

  const showArchive = canArchiveVendor(authorization, vendor?.bu);
  const archived = vendor?.lifecycleStatus === 'archived';

  const handleLifecycle = async () => {
    if (!vendor || !showArchive) {
      return;
    }
    setArchiving(true);
    try {
      const hostnameForCall = vendor.hostname || 'default';
      const result = archived
        ? await vendorApi.restoreVendor(vendor.vendorId, hostnameForCall)
        : await vendorApi.archiveVendor(vendor.vendorId, hostnameForCall);
      setVendor((current) => (
        current
          ? {
              ...current,
              lifecycleStatus: result.lifecycleStatus,
              updatedAt: result.updatedAt || current.updatedAt,
            }
          : current
      ));
      message.success(result.lifecycleStatus === 'archived' ? 'Vendor archived' : 'Vendor restored');
    } catch (error: unknown) {
      if (error instanceof VendorUpdateError) {
        const text = error.message || 'Update failed';
        message.error(error.errorCode ? `${error.errorCode}: ${text}` : text);
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'Failed to update vendor';
      message.error(errorMessage);
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        {backButton}
        <div style={{ marginTop: 48 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="404"
          title="Vendor not found"
          subTitle={`${decodeURIComponent(vendorId || '')} / ${hostname}`}
          extra={backButton}
        />
      </div>
    );
  }

  if (loadError || !vendor) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="error"
          title="Unable to load vendor"
          extra={backButton}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        {backButton}
        <ReinviteVendorButton
          vendor={vendor}
          authorization={authorization}
          onReinvited={() => loadVendor(false)}
        />
        {showArchive ? (
          <Popconfirm
            title={archived ? 'Restore vendor' : 'Archive vendor'}
            description={
              archived
                ? 'Restoring makes this vendor active again. SFTP access, ad hoc transfers, and default directory lists apply as they do for any other active vendor.'
                : 'Archiving removes this vendor\'s SFTP access, rejects ad hoc transfers, and hides the vendor from default directory lists. Continue?'
            }
            okText={archived ? 'Restore' : 'Archive'}
            cancelText="Cancel"
            okButtonProps={{ danger: !archived }}
            onConfirm={handleLifecycle}
          >
            <Button danger={!archived} loading={archiving}>
              {archived ? 'Restore Vendor' : 'Archive Vendor'}
            </Button>
          </Popconfirm>
        ) : null}
      </Space>
      <Title level={2} style={{ marginTop: 0 }}>
        {vendor.vendorId}
      </Title>
      <VendorEditForm
        vendor={vendor}
        authorization={authorization}
        onUpdated={setVendor}
      />
      <Card>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2 }}>
          <Descriptions.Item label="Vendor ID">{vendor.vendorId}</Descriptions.Item>
          <Descriptions.Item label="Hostname">{vendor.hostname || 'default'}</Descriptions.Item>
          <Descriptions.Item label="BU">{vendor.bu || '—'}</Descriptions.Item>
          <Descriptions.Item label="SFTP username">{vendor.sftpUsername || '—'}</Descriptions.Item>
          <Descriptions.Item label="Vendor email">{vendor.vendorEmail || '—'}</Descriptions.Item>
          <Descriptions.Item label="Default Success Email">{vendor.successEmail || '—'}</Descriptions.Item>
          <Descriptions.Item label="Default Failure Email">{vendor.failureEmail || '—'}</Descriptions.Item>
          <Descriptions.Item label="Outbound bucket">{vendor.outboundBucket || '—'}</Descriptions.Item>
          <Descriptions.Item label="Outbound prefix">{vendor.outboundPrefix || '—'}</Descriptions.Item>
          <Descriptions.Item label="Onboarding">{onboardingTag(vendor.onboardingStatus)}</Descriptions.Item>
          <Descriptions.Item label="Lifecycle">
            {vendor.lifecycleStatus === 'archived' ? <Tag color="purple">Archived</Tag> : 'Active'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated">{formatTimestamp(vendor.updatedAt)}</Descriptions.Item>
          <Descriptions.Item label="Onboarded">{formatTimestamp(vendor.onboardedAt)}</Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default OperatorVendorDetailPage;
